import Bugsnag from "@bugsnag/js";
import classNames from "classnames";
import { createBrowserHistory } from "history";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import { useGlobalLoader } from "@Components/WithGlobalLoader";
import WithLocale from "@Components/WithLocale";
import useCheerzApiProvider from "@Hooks/useCheerzApiProvider";
import { ApiErrorResponse } from "@Types/api/error";
import DraftContent from "@Types/draft";
import UserContent from "@Types/user";
import { convertDraftsFromApiToContent } from "@Utils/draft";
import { getMetaContent } from "@Utils/meta";
import { goToCategories } from "@Utils/navigation";
import { convertToIdentifyProperties, setDataLayerTrack } from "@Utils/track";
import { ReactComponent as IconBox } from "./box.svg";
import DraftDeleteModal from "./DraftDeleteModal";
import DraftItem from "./DraftItem";
import "./style.scss";

const history = createBrowserHistory();

interface Props {
  drafts: DraftContent[];
  onRemoveDraft: (draft: DraftContent | null) => void;
}

interface ContainerProps {
  userData: UserContent;
}

export const Drafts: FunctionComponent<Props> = ({ drafts, onRemoveDraft }) => {
  const intl = useIntl();
  const [removeDraft, setRemoveDraft] = useState<DraftContent | null>(null);
  const [showDraftDeleteModal, setShowDraftDeleteModal] = useState(false);

  const handleClick = () => {
    goToCategories(intl.locale, "all");
  };

  const handleConfirmRemoveDraft = () => {
    onRemoveDraft(removeDraft);
    handleDismissDeleteModal();
  };

  const handleDismissDeleteModal = () => {
    setRemoveDraft(null);
    setShowDraftDeleteModal(false);
  };

  const handleRemoveDraft = (draft: DraftContent) => {
    setRemoveDraft(draft);
    setShowDraftDeleteModal(true);
  };

  return (
    <div className="Drafts">
      {drafts.length > 0 ? (
        <div className="Drafts-list">
          <div className="Drafts-list-title">
            {intl.formatMessage({ id: "cheerz_Drafts_list_title" })}
          </div>
          <div className="Drafts-list-description">
            {intl.formatMessage({ id: "cheerz_Drafts_list_description" })}
          </div>
          <div
            className={classNames("Drafts-list-items", {
              "Drafts-list-items--multiDraft": drafts.length > 1,
            })}
          >
            {drafts.map((draft: DraftContent) => (
              <DraftItem draft={draft} key={draft.id} onRemoveDraft={handleRemoveDraft} />
            ))}
          </div>
          <DraftDeleteModal
            display={showDraftDeleteModal}
            onConfirm={handleConfirmRemoveDraft}
            onDismiss={handleDismissDeleteModal}
          />
        </div>
      ) : (
        <div className="Drafts-empty">
          <IconBox />
          <div className="Drafts-empty-title">
            {intl.formatMessage({ id: "cheerz_Drafts_empty_title" })}
          </div>
          <div className="Drafts-empty-description">
            {intl.formatMessage({ id: "cheerz_Drafts_empty_description" })}
          </div>
          <ButtonSmily onClick={handleClick} size="m" type="primary">
            {intl.formatMessage({ id: "cheerz_Drafts_empty_button" })}
          </ButtonSmily>
        </div>
      )}
    </div>
  );
};

const DraftsContainer: FunctionComponent<ContainerProps> = ({ userData }) => {
  const [drafts, setDrafts] = useState<DraftContent[]>([]);
  const cheerzApiProvider = useCheerzApiProvider();
  const { setLoading } = useGlobalLoader();
  const intl = useIntl();

  useEffect(() => {
    cheerzApiProvider
      .getDrafts()
      .then((drafts) => {
        setDrafts(convertDraftsFromApiToContent(drafts));
        setDataLayerTrack("Global", {
          currency: userData.currency,
          environment: getMetaContent("cheerz-env") === "production" ? "production" : "test",
          locale: intl.locale,
        });
        setDataLayerTrack("Identify", convertToIdentifyProperties(userData));
        setDataLayerTrack("Page", {
          category: "Dashboard",
          name: "Drafts",
          path: `/${intl.locale}/dashboard/drafts`,
          version: "A",
        });
        setLoading(false);
      })
      .catch((error: Error) => {
        Bugsnag.notify(error);
      });
  }, [cheerzApiProvider]);

  const handleRemoveDraft = (draft: DraftContent | null) => {
    if (draft) {
      setDataLayerTrack("Saved Products Remove Clicked Data", {
        productTag: draft.product.tag,
      });

      cheerzApiProvider
        .removeDraft(draft.id)
        .then(() => {
          return cheerzApiProvider.getDrafts();
        })
        .then((drafts) => setDrafts(convertDraftsFromApiToContent(drafts)))
        .catch((error: ApiErrorResponse) => {
          Bugsnag.notify(
            Error(
              error.response?.data?.error_description[0]
                ? `${error.response?.data?.error_description[0]}`
                : "Error on removeDraft call"
            )
          );
        });
    }
  };

  return (
    <WithLocale history={history}>
      <Drafts drafts={drafts} onRemoveDraft={handleRemoveDraft} />
    </WithLocale>
  );
};

export default DraftsContainer;
